import { Tabs } from '@mantine/core';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import React, { useMemo, useState } from 'react';
import { colors } from '../theme/colors';
import { LogStatusGrid } from './LogStatusGrid';
import { LogProjectsGrid } from './LogProjectsGrid';
import { LeistungsnachweisPreview } from './LeistungsnachweisPreview';
import { ZmSyncAdmin } from './ZmSyncAdmin';
import { getLogStatusSummary, getLogProjects, reOpenWeekEntries } from '../services/timeEntryService';
import { getCalendarWeek } from '../utils/dateUtils';

export const AdminSettings: React.FC = () => {
  const navigate = useNavigate();
  const router = useRouterState();
  const queryClient = useQueryClient();

  const currentPath = router.location.pathname;
  const activeTab = useMemo(() => {
    if (currentPath.includes('/leistungsnachweis')) return 'leistungsnachweis';
    if (currentPath.includes('/zm-sync')) return 'zm-sync';
    if (currentPath.includes('/project-hours')) return 'employee-project-hours';
    return 'employee-logged-hours';
  }, [currentPath]);

  const currentWeek = useMemo(() => getCalendarWeek(new Date()), []);
  const [selectedWeek, setSelectedWeek] = useState<string | undefined>(currentWeek);

  // TanStack Query for fetching log status summary
  const {
    data: logStatusData,
    isPending: isLogStatusPending,
    isFetching: isLogStatusFetching,
    error: logStatusError,
    refetch: refetchLogStatus,
  } = useQuery({
    queryKey: ['log-status-summary', selectedWeek],
    queryFn: () => getLogStatusSummary(selectedWeek),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    placeholderData: keepPreviousData,
  });

  const isLogStatusInitialLoad = isLogStatusPending && logStatusData === undefined;

  // TanStack Query for fetching log projects
  const {
    data: logProjectsData,
    isLoading: isLoadingLogProjects,
    error: logProjectsError,
    refetch: refetchLogProjects,
  } = useQuery({
    queryKey: ['log-projects'],
    queryFn: getLogProjects,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
  });

  // Use API data or fallback to empty arrays
  const logStatusSummary = logStatusData || [];
  const logProjects = logProjectsData || [];

  const handleReOpen = async (employeeId: string, calendarWeek: string) => {
    try {
      await reOpenWeekEntries(employeeId, calendarWeek);
      await queryClient.invalidateQueries({ queryKey: ['log-status-summary'] });
    } catch (error) {
      alert(`Failed to re-open week entries: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <div
      style={{
        backgroundColor: colors.neutral.white,
        borderRadius: '8px',
        padding: '1rem',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Tabs
        value={activeTab}
        onChange={(value) => {
          if (value === 'employee-logged-hours') {
            navigate({ to: '/admin-settings/logged-hours' });
          } else if (value === 'employee-project-hours') {
            navigate({ to: '/admin-settings/project-hours' });
          } else if (value === 'leistungsnachweis') {
            navigate({ to: '/admin-settings/leistungsnachweis' });
          } else if (value === 'zm-sync') {
            navigate({ to: '/admin-settings/zm-sync' });
          }
        }}
        style={{ width: '100%' }}
      >
        <Tabs.List
          style={{
            borderBottom: `2px solid ${colors.neutral.grey[200]}`,
            marginBottom: '1rem',
            gap: '0.25rem',
          }}
        >
          <Tabs.Tab value="employee-logged-hours">Employee Logged Hours</Tabs.Tab>
          <Tabs.Tab value="employee-project-hours">Employee Project Hours</Tabs.Tab>
          <Tabs.Tab value="leistungsnachweis">Leistungsnachweis</Tabs.Tab>
          <Tabs.Tab value="zm-sync">ZM Sync</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="employee-logged-hours">
          {isLogStatusInitialLoad ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '2rem',
                color: colors.secondary.text,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    border: `2px solid ${colors.primary.background}`,
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                Loading log status...
              </div>
            </div>
          ) : logStatusError ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '2rem',
                color: colors.accent.red,
              }}
            >
              <p style={{ margin: '0 0 1rem 0' }}>
                Failed to load log status: {logStatusError.message}
              </p>
              <button
                onClick={() => refetchLogStatus()}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: colors.primary.background,
                  color: colors.neutral.white,
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Retry
              </button>
            </div>
          ) : (
            <LogStatusGrid
              logStatusData={logStatusSummary}
              isFetching={isLogStatusFetching}
              onReOpen={handleReOpen}
              selectedWeek={selectedWeek}
              onWeekChange={setSelectedWeek}
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="employee-project-hours">
          {isLoadingLogProjects ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '2rem',
                color: colors.secondary.text,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    border: `2px solid ${colors.primary.background}`,
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                Loading log projects...
              </div>
            </div>
          ) : logProjectsError ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '2rem',
                color: colors.accent.red,
              }}
            >
              <p style={{ margin: '0 0 1rem 0' }}>
                Failed to load log projects: {logProjectsError.message}
              </p>
              <button
                onClick={() => refetchLogProjects()}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: colors.primary.background,
                  color: colors.neutral.white,
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Retry
              </button>
            </div>
          ) : (
            <LogProjectsGrid logProjectsData={logProjects} isLoading={isLoadingLogProjects} />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="leistungsnachweis">
          {isLoadingLogProjects ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '2rem',
                color: colors.secondary.text,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    border: `2px solid ${colors.primary.background}`,
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                Loading log projects...
              </div>
            </div>
          ) : logProjectsError ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '2rem',
                color: colors.accent.red,
              }}
            >
              <p style={{ margin: '0 0 1rem 0' }}>
                Failed to load log projects: {logProjectsError.message}
              </p>
              <button
                onClick={() => refetchLogProjects()}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: colors.primary.background,
                  color: colors.neutral.white,
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Retry
              </button>
            </div>
          ) : (
            <LeistungsnachweisPreview logProjectsData={logProjects} />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="zm-sync">{activeTab === 'zm-sync' && <ZmSyncAdmin />}</Tabs.Panel>
      </Tabs>
    </div>
  );
};
